import { activateAccountRequest } from '@/lib/recepthor-api';
import { Button, Input } from '@/ui';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import BrandPane from './BrandPane.jsx';
import { validatePassword } from './passwordValidation';
import TermsAcceptField from './TermsAcceptField.jsx';
import TermsAndConditions, {
  FALLBACK_TYC_VERSION,
} from './terms-and-conditions.jsx';
import { useTermsCatalog } from './useTermsCatalog.js';

function InvalidLinkPane() {
  return (
    <div className="login-form-pane">
      <div className="login-form">
        <p className="login-eyebrow">Activación</p>
        <h1 className="login-heading">Enlace inválido</h1>
        <p className="recover-subtitle">
          El enlace de activación no es válido. Verifica el link recibido por
          correo.
        </p>
        <Link to="/login" className="login-forgot">
          Volver a inicio de sesión
        </Link>
      </div>
    </div>
  );
}

const MSG_TERMS_LOCAL =
  'Debes aceptar los términos y condiciones para continuar.';

function ActivateForm({ token }) {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Read at flow init (this form mounting is the flow init), not lazily when
  // the modal opens — the submit needs the version whether or not the user
  // ever opens the T&C modal. Falls back to the last-known hardcoded version
  // only if the catalog read fails or is incomplete (no version/pdfUrl — same
  // validity gate as TermsAndConditions); the API rejects a stale version with
  // a 404, already handled below. Gate accept/submit on catalogLoading so the
  // user cannot accept fallback copy that a late success would replace.
  const { catalog, loading: catalogLoading } = useTermsCatalog();
  const tycVersion =
    catalog?.version && catalog?.pdfUrl
      ? catalog.version
      : FALLBACK_TYC_VERSION;

  // The terms message belongs to the submit that raised it: ticking the box
  // again retires it, unticking leaves it. Any other error stays.
  const handleTermsChange = () => {
    const next = !termsAccepted;
    setTermsAccepted(next);
    if (next) setError((prev) => (prev === MSG_TERMS_LOCAL ? '' : prev));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (catalogLoading) {
      return;
    }

    const validationError = validatePassword(password);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    if (!termsAccepted) {
      setError(MSG_TERMS_LOCAL);
      return;
    }

    setLoading(true);
    try {
      const response = await activateAccountRequest({
        token,
        password,
        termsAccepted,
        tycVersion,
      });

      if (response.ok) {
        navigate('/login', { state: { accountActivated: true } });
        return;
      }

      if (response.status === 404) {
        setError(
          'No pudimos validar la versión de los términos. Recarga la página o contacta a soporte.',
        );
      } else if (response.status === 400) {
        setError(
          'El enlace de activación es inválido o ha expirado. Contacta a soporte para recibir uno nuevo.',
        );
      } else {
        setError('Error al activar la cuenta. Intenta nuevamente.');
      }
    } catch {
      setError('Error de conexión. Verifica tu internet e intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-form-pane">
      <form className="login-form" onSubmit={handleSubmit}>
        <p className="login-eyebrow">Activación</p>
        <h1 className="login-heading">Activa tu cuenta</h1>
        <p className="recover-subtitle">
          Crea una contraseña para completar la activación.
        </p>
        <div className="login-fields">
          <div className="login-field">
            <label className="login-label" htmlFor="activate-password">
              Nueva contraseña *
            </label>
            <Input
              id="activate-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <div className="login-field">
            <label className="login-label" htmlFor="activate-confirm">
              Confirmar contraseña *
            </label>
            <Input
              id="activate-confirm"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>
          <TermsAcceptField
            checked={termsAccepted}
            onChange={handleTermsChange}
            disabled={catalogLoading}
            onOpenTerms={() => setShowTerms(true)}
          />
          {error && (
            <p className="login-alert login-alert--err" role="alert">
              {error}
            </p>
          )}
          <Button
            type="submit"
            variant="seal"
            size="lg"
            disabled={!termsAccepted || loading || catalogLoading}
            className="login-submit"
          >
            {loading ? 'Activando…' : 'Activar cuenta'}
          </Button>
        </div>
        <Link to="/login" className="login-forgot">
          Volver a inicio de sesión
        </Link>
      </form>
      <TermsAndConditions
        catalog={catalog}
        loading={catalogLoading}
        show={showTerms}
        onClose={() => setShowTerms(false)}
      />
    </div>
  );
}

ActivateForm.propTypes = {
  token: PropTypes.string.isRequired,
};

export default function ActivateAccount() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  return (
    <div className="login-page">
      <BrandPane />
      {token ? <ActivateForm token={token} /> : <InvalidLinkPane />}
    </div>
  );
}
